/**
 * resourceUIRegistry.js — Project Resource architecture, Phase 7 cleanup
 * (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
 *
 * Consolidates what were two separate UI-side registration tables —
 * resourceManagerRegistry.js (Phase 5) and resourcePropertyOverrides.js
 * (Phase 3) — into one, since both were already keyed by the same
 * `resourceType`, lived at the same layer (plain data + component imports,
 * no runtime registration calls), and adding a full resource type meant
 * editing both in lockstep anyway. This is deliberately NOT merged with
 * src/stores/resourceRegistry.js's adapter map: that one is populated
 * imperatively at app bootstrap (main.js's registerAdapter() calls, timed
 * around Pinia's installation) and is consumed by non-UI code paths too —
 * folding Vue component references into it would mean the store/adapter
 * layer importing component files, which blurs a layering boundary worth
 * keeping (adapters should stay usable without pulling in the component
 * tree). See docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md's Phase 7 addendum
 * for the full reasoning.
 *
 * Two independent UI consumers read this table:
 *  - ResourceManagerShell.vue builds the Resources section's navigation from
 *    `displayName`/`icon`/`managerComponent`, forwarding `context` through
 *    `getProps`/`getListeners`.
 *  - PropertiesPanel.vue's generic resource-property loop calls
 *    getPropertyEditorComponent(resourceType) to decide whether a
 *    `type: 'resource'` schema property gets a specialized editor or falls
 *    back to the bare ResourcePicker.
 * Neither consumer has any built-in knowledge of datasets, fonts, images, or
 * any other resource type — adding one requires only a new entry below.
 */
import { markRaw } from 'vue'
import DatasetManager from '../datasets/DatasetManager.vue'
import ChartDatasetPicker from '../datasets/ChartDatasetPicker.vue'
import FontManager from '../fonts/FontManager.vue'
import FontFamilyPicker from '../fonts/FontFamilyPicker.vue'
import ImageTab from '../imagemodal/ImageTab.vue'
import ImageResourcePicker from '../imagemodal/ImageResourcePicker.vue'
import AudioManager from '../audio/AudioManager.vue'
import SynthManager from '../synth/SynthManager.vue'
import Model3dManager from '../model3d/Model3dManager.vue'

// markRaw() keeps these component definitions out of Vue's reactivity system.
// The registry is read from reactive contexts (e.g. passed as the `registry`
// prop to ResourceManagerShell), and without markRaw Vue would wrap each
// component object in a reactive proxy — pointless overhead that also triggers
// the "received a Component that was made a reactive object" warning.
export const RESOURCE_UI_REGISTRY = [
  {
    type: 'dataset',
    displayName: 'Datasets',
    icon: 'fa-solid fa-database',
    managerComponent: markRaw(DatasetManager),
    // ChartDatasetPicker owns legitimate Chart/Legend-specific behavior
    // alongside selection (series preview, "no dataset linked" warning, an
    // "open in library" link, series/point_count synchronization) — see its
    // own file comment. It already delegates the actual dropdown to the
    // bare ResourcePicker internally.
    propertyEditorComponent: markRaw(ChartDatasetPicker),
    getProps: (ctx) => ({
      settings: ctx.settings,
      objectlayer: ctx.objectlayer,
      requestOpenUUID: ctx.requestOpenUUID ?? null,
    }),
    getListeners: (ctx) => ({
      'navigate-to-widget': ctx.onNavigateToWidget,
    }),
  },
  {
    type: 'font',
    displayName: 'Fonts',
    icon: 'fa-solid fa-font',
    managerComponent: markRaw(FontManager),
    // FontFamilyPicker merges the fixed internal theme/icon fonts
    // (src/utils/internalFonts.js) alongside project fonts, and stores the
    // wire-format string directly — see label.js's `font` property comment.
    propertyEditorComponent: markRaw(FontFamilyPicker),
    getProps: (ctx) => ({
      objectlayer: ctx.objectlayer,
      requestOpenUUID: ctx.requestOpenFontUUID ?? null,
    }),
    getListeners: (ctx) => ({
      'navigate-to-widget': ctx.onNavigateToWidget,
    }),
  },
  {
    type: 'image',
    displayName: 'Images',
    icon: 'fa-solid fa-image',
    // ImageTab.vue IS the existing Image Manager (see AppModals.vue's
    // standalone "Image store" popup, which mounts it the same way with the
    // same default editable/clearEnabled props). Reused unchanged here —
    // this is a second entry point onto the same shared imageStore, not a
    // replacement for the existing standalone modal.
    managerComponent: markRaw(ImageTab),
    // ImageResourcePicker owns the thumbnail-browser selection flow — a
    // plain <select> dropdown can't browse images by thumbnail.
    propertyEditorComponent: markRaw(ImageResourcePicker),
    getProps: (ctx) => ({
      refreshTrigger: ctx.imageStoreUpdated ?? 0,
    }),
    getListeners: (ctx) => ({
      imageDeleted: ctx.onImageDeleted,
      imageUpload: ctx.onImageUpload,
    }),
  },
  {
    type: 'audio',
    displayName: 'Audio',
    icon: 'fa-solid fa-volume-high',
    managerComponent: markRaw(AudioManager),
    // No propertyEditorComponent: the audio-resource selector is the bare
    // ResourcePicker (PropertiesPanel renders it inside the Action accordion
    // when the chosen command is `audio.play`) — there is nothing
    // audio-specific about picking a clip by name.
    getProps: (ctx) => ({
      objectlayer: ctx.objectlayer,
    }),
  },
  {
    type: 'model3d',
    displayName: '3D Models',
    icon: 'fa-solid fa-cube',
    managerComponent: markRaw(Model3dManager),
  },
  {
    type: 'synth',
    displayName: 'Synth',
    icon: 'fa-solid fa-wave-square',
    managerComponent: markRaw(SynthManager),
    getProps: (ctx) => ({ settings: ctx.settings }),
  },
]

/** The specialized property-editor component for `resourceType`, or null to fall back to the bare ResourcePicker. */
export function getPropertyEditorComponent(resourceType) {
  return RESOURCE_UI_REGISTRY.find((entry) => entry.type === resourceType)?.propertyEditorComponent ?? null
}
